import { isNativePlatform } from '../runtime'
import { nativeAdapterUnavailable } from './unavailable'
import { NativeAppCheck, NATIVE_APP_CHECK_PLUGIN_NAME } from './nativeAppCheck'

/**
 * App Check attestation capability (#1690 adapter contract; #2372, slice 2 of
 * #2370). On web, App Check is reCAPTCHA Enterprise and lives entirely in
 * `services/firebase/appCheck.js` — this adapter has no web behaviour and says
 * so. On native it is App Attest / DeviceCheck (iOS) or Play Integrity
 * (Android), reached through the typed `NativeAppCheck` bridge.
 *
 * Whether the native plugin is linked is asked of Capacitor itself
 * (`isPluginAvailable` is true only once the native side has published a
 * header for the plugin), NOT inferred from a failed call: `initAppCheck` has
 * to decide synchronously at startup, and a never-linked plugin (#2376) must
 * be a loud, deliberate "unavailable" rather than a rejected promise on every
 * token refresh.
 */
export function isAppCheckTokenAvailable() {
  if (!isNativePlatform()) {
    return false
  }
  try {
    return window.Capacitor.isPluginAvailable(NATIVE_APP_CHECK_PLUGIN_NAME) === true
  } catch {
    return false
  }
}

/**
 * The one typed method (spec.md §11.1: no arbitrary path/callable). Resolves
 * to the shape firebase's `CustomProvider.getToken` expects.
 *
 * @return {Promise<{token: string, expireTimeMillis: number}>}
 */
export async function getAppCheckToken() {
  if (!isAppCheckTokenAvailable()) {
    return nativeAdapterUnavailable('appCheck.getAppCheckToken')
  }
  const result = await NativeAppCheck.getToken()
  if (!result || typeof result.token !== 'string' || !result.token || !Number.isFinite(result.expireTimeMillis)) {
    throw new Error('[platform] NativeAppCheck.getToken returned a malformed result')
  }
  return { token: result.token, expireTimeMillis: result.expireTimeMillis }
}
